import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { guid } from '@/mixins/guid';
import linkPage from '@editor/droplets/_bubble-options/components/link-page';
import { colShape } from '../../utils';

export default Vue.component('grid-options', {
  template: require('./template.html'),
  mixins: [guid],
  components: {
    linkPage,
  },
  computed: {
    ...mapGetters(['viewport']),
    gridContainer() {
      const selector = _.find(this.droplet.mediaQueries, { selector: '> .grid-container' });
      const property = selector ? _.find(selector.properties, { property: 'grid-template-columns' }) : false;
      return property;
    },
  },
  data() {
    return {
      startX: 0,
      autoFitOpen: false,
      autoFit: {
        desktop: false,
        tablet: false,
        mobile: false,
      },
      minLimit: 1,
      maxLimit: 16,
      display: {
        minAutoWidth: '',
        minAutoWidth_unit: '',
      },
      activeColumn: {},
      activeLink: null,
      activeLinkRowIndex: 0,
      activeLinkColIndex: 0,
      loading: false,
      imageFiles: [],
      activeImg: null,
      activeImgName: '',
      showFiles: false,
      initial: null,
      prefix: 'drz_grid_',
      bucketPath: '_droplet-assets/grid/',
      thumbnails: '_droplet-assets/_thumbnails/grid/',
      showConfirm: false,
      hoverDel: false,
      activeIndex: null,
      dragging: false,
      $handle: null,
      activeColIndex: null,
      allowedCols: [],
      slideDirection: null,
      allowedDrop: false,
      mousedown: false,
      lastHovered: {
        rowIndex: null,
        colIndex: null,
      },
      dragHover: {
        rowIndex: null,
        colIndex: null,
      },
      editError: false,
    };
  },
  name: 'grid-options',
  props: ['droplet', 'show-edit-bubble'],
  watch: {
    viewport() {
      if (this.showEditBubble) {
        this.setInitialValues();
      }
    },
    showEditBubble(open) {
      if (open) {
        this.setInitialValues();
      } else {
        this.activeLink = null;
      }
    },
    activeLink(column) {
      this.$emit('set-active-link', column);
    },
  },
  methods: {
    setInitialValues() {
      const minAutoWidth = this.getMinWidth({
        $vm: this,
        selector: '> .grid-container',
        property: 'grid-template-columns',
        defaultUnit: 'px',
        defaultVal: 200,
      });
      this.display.minAutoWidth = minAutoWidth.val;
      this.display.minAutoWidth_unit = minAutoWidth.unit;
      // autoFit values
      this.autoFit.desktop = this.droplet.autoFit.desktop;
      this.autoFit.tablet = this.droplet.autoFit.tablet;
      this.autoFit.mobile = this.droplet.autoFit.mobile;
    },
    changeCSS(params) {
      const payload = {};
      payload.drp = this.droplet;
      payload.val = this.buildVal(params);
      payload.selector = params.selector;
      payload.property = params.property;
      payload.viewport = this.viewport;
      this._debounce(() => {
        this.$store.dispatch('changeDropletCss', payload);
        this.$emit('css-generator');
      }, 250);
    },
    buildVal(params) {
      const num = params.event.target.value;
      const unit = params.unit;
      return `repeat(auto-fit, minmax(${num}${unit}, 1fr))`;
    },
    updateAutoFit() {
      // set the initial viewport status for this
      this.$store.dispatch('setDropletVal', {
        drp: this.droplet.autoFit,
        key: this.viewport,
        val: this.autoFit[this.viewport],
      });
      // add fallbacks if needed
      ['tablet', 'mobile'].forEach((viewport) => {
        if (this.viewport === 'desktop' && this.gridContainer[viewport] === 'default') {
          this.$store.dispatch('setDropletVal', {
            drp: this.droplet.autoFit,
            key: viewport,
            val: this.autoFit[this.viewport],
          });
        }
      });
      if (this.autoFit[this.viewport]) {
        this.unsetGridAreas();
      }
      if (!this.autoFit[this.viewport]) {
        this.calculateGridAreas();
      }
    },
    calculateGridAreas() {
      // replace old container grid value
      this.$store.dispatch('changeDropletCss', {
        drp: this.droplet,
        val: `repeat(${this.droplet.gridColumns}, 1fr)`,
        selector: '> .grid-container',
        property: 'grid-template-columns',
        viewport: this.viewport,
      });
      _.forEach(this.droplet.rows, (row) => {
        _.forEach(row.columns, (column) => {
          // remove any covered values
          this.$store.dispatch('setDropletVal', {
            drp: column.covered,
            key: this.viewport,
            val: false,
          });
          // update column css
          const rule = _.find(column.mediaQueries, mq => mq.property === 'grid-area');
          this.$store.dispatch('changeDropletCss', {
            drp: column,
            val: this.getInitialGrid(column),
            parent: rule,
            viewport: this.viewport,
          });
        });
      });
      this.$emit('css-generator');
    },
    unsetGridAreas() {
      this.$store.dispatch('changeDropletCss', {
        drp: this.droplet,
        val: 'repeat(auto-fit, minmax(200px, 1fr))',
        selector: '> .grid-container',
        property: 'grid-template-columns',
        viewport: this.viewport,
      });
      _.forEach(this.droplet.rows, (row) => {
        _.forEach(row.columns, (column) => {
          // remove any covered values
          this.$store.dispatch('setDropletVal', {
            drp: column.covered,
            key: this.viewport,
            val: false,
          });
          // update column css
          const rule = _.find(column.mediaQueries, mq => mq.property === 'grid-area');
          this.$store.dispatch('changeDropletCss', {
            drp: column,
            val: 'initial',
            parent: rule,
            viewport: this.viewport,
          });
        });
      });
      this.$emit('css-generator');
    },
    updateGridColumns(type) {
      const columns = this.droplet.gridColumns;
      const newTotal = type === 'add' ? columns + 1 : columns - 1;
      if ((type === 'remove' && newTotal > 0) || (type === 'add' && newTotal <= this.maxLimit)) {
        // if a row has the same amount of columns as column grid spaces, we should
        // remove the last one if it's hidden/removed
        if (type === 'remove') {
          _.forEach(this.droplet.rows, (row) => {
            const colsInRow = row.columns.length;
            const lastCol = row.columns[colsInRow - 1];
            if (colsInRow >= columns && lastCol.removed && !lastCol.covered[this.viewport]) {
              this.$store.dispatch('spliceDropletVal', {
                array: row.columns,
                index: colsInRow - 1,
              });
            }
          });
        }
        // this should only add a new repeat value for grid container
        if (!this.autoFit[this.viewport]) {
          this.$store.dispatch('changeDropletCss', {
            drp: this.droplet,
            val: `repeat(${newTotal}, 1fr)`,
            selector: '> .grid-container',
            property: 'grid-template-columns',
            viewport: this.viewport,
          });
        }
        this.$store.dispatch('setDropletVal', {
          drp: this.droplet,
          key: 'gridColumns',
          val: newTotal,
        });
        this.$emit('css-generator');
      }
    },
    addGridRow() {
      const columns = [];
      const rows = this.droplet.rows.length;
      const autoFit = this.autoFit[this.viewport];
      _.times(this.droplet.gridColumns, (i) => {
        const column = colShape({
          autoFit,
          rowStart: `${rows + 1}`,
          colStart: `${i + 1}`,
          rowEnd: `${rows + 2}`,
          colEnd: `${i + 2}`,
        }, this.autoFit)();
        columns.push(column);
      });
      this.$store.dispatch('pushDropletVal', {
        array: this.droplet.rows,
        val: { columns },
      });
      this.$emit('css-generator');
    },
    removeGridRow(i) {
      if (this.viewport === 'desktop') {
        const rows = this.droplet.rows;
        this.editError = false;
        if (rows.length > 1) {
          this.$store.dispatch('spliceDropletVal', {
            array: rows,
            index: i,
          });
          // Need to bump up any row values for rows under removed one
          if (i < rows.length) {
            _.forEach(this.droplet.rows, (row, rIndex) => {
              if (rIndex >= i) {
                // need to keep all other col values entact
                _.forEach(row.columns, (column) => {
                  //  update grid coords
                  const grid = _.cloneDeep(column.grid);
                  grid.rowStart = `${rIndex + 1}`;
                  grid.rowEnd = `${rIndex + 2}`;
                  this.$store.dispatch('setDropletVal', {
                    drp: column,
                    key: 'grid',
                    val: grid,
                  });
                  // update any viewports that are set
                  const parent = _.find(column.mediaQueries, mq => mq.property === 'grid-area');
                  ['desktop', 'tablet', 'mobile'].forEach((viewport) => {
                    const val = parent[viewport];
                    if (val !== 'default' || val !== 'initial') {
                      const values = val.split('/').map(n => n.trim());
                      values[0] = `${rIndex + 1}`;
                      values[2] = `${rIndex + 2}`;
                      this.$store.dispatch('changeDropletCss', {
                        drp: this.droplet,
                        val: values.join(' / '),
                        parent,
                        viewport,
                      });
                    }
                  });
                });
              }
            });
          }
          this.$emit('css-generator');
        } else {
          this.editError = 'There must be at least 1 row present.';
        }
      }
    },
    updateColumn(params) {
      const rule = _.find(params.column.mediaQueries, mq => mq.property === 'grid-area');
      if (rule) {
        const str = rule[this.viewport];
        const v = str !== 'default' ? str : rule.desktop;
        const values = v.split('/').map(n => n.trim());
        const originIndex = params.column.grid.colStart - 1;
        const initialRangeStart = values[1] - 1;
        const initialRangeEnd = values[3] - 2;
        const initialRange = _.filter(
          _.range(initialRangeStart, initialRangeEnd + 1), n => n !== originIndex);
        if (this.slideDirection === 'left') {
          values[1] = `${params.endIndex + 1}`;
        }
        if (this.slideDirection === 'right') {
          values[3] = `${params.endIndex + 2}`;
        }
        const rangeStart = values[1] - 1;
        const rangeEnd = values[3] - 2;
        // if initial ranges aren't in current range, we need to unmark covered
        const endRange = _.range(rangeStart, rangeEnd + 1);
        const uncoverList = initialRange.filter(e => !endRange.includes(e));
        _.forEach(uncoverList, (i) => {
          this.$store.dispatch('setDropletVal', {
            drp: params.columns[i].covered,
            key: this.viewport,
            val: false,
          });
          if (this.viewport === 'desktop' && rule.tablet === 'default') {
            this.$store.dispatch('setDropletVal', {
              drp: params.columns[i].covered,
              key: 'tablet',
              val: false,
            });
          }
          if (this.viewport === 'desktop' && rule.mobile === 'default') {
            this.$store.dispatch('setDropletVal', {
              drp: params.columns[i].covered,
              key: 'mobile',
              val: false,
            });
          }
        });
        // here we need to mark columns as overlapped if spread over
        _.forEach(endRange, (i) => {
          if (i !== originIndex) {
            this.$store.dispatch('setDropletVal', {
              drp: params.columns[i].covered,
              key: this.viewport,
              val: `i-${originIndex}`,
            });
            // here we need to also default covered vals if other vp's inhere
            if (this.viewport === 'desktop' && rule.tablet === 'default') {
              this.$store.dispatch('setDropletVal', {
                drp: params.columns[i].covered,
                key: 'tablet',
                val: `i-${originIndex}`,
              });
            }
            if (this.viewport === 'desktop' && rule.mobile === 'default') {
              this.$store.dispatch('setDropletVal', {
                drp: params.columns[i].covered,
                key: 'mobile',
                val: `i-${originIndex}`,
              });
            }
          }
        });
        this.$store.dispatch('changeDropletCss', {
          drp: this.droplet,
          val: values.join(' / '),
          parent: rule,
          viewport: this.viewport,
        });
        this.$emit('css-generator');
      }
    },
    addColumn(row, rIndex) {
      if (this.viewport === 'desktop') {
        this.editError = false;
        const total = row.columns.length;
        const colSpaces = this.droplet.gridColumns;
        if (total < colSpaces && total < this.maxLimit) {
          const autoFit = this.autoFit[this.viewport];
          const newCol = colShape({
            autoFit,
            rowStart: `${rIndex + 1}`,
            colStart: `${total + 1}`,
            rowEnd: `${rIndex + 2}`,
            colEnd: `${total + 2}`,
          }, this.autoFit)();
          // append new column to selected row
          this.$store.dispatch('pushDropletVal', {
            array: row.columns,
            val: newCol,
          });
          this.$emit('css-generator');
        }
        if (total >= colSpaces) {
          this.editError = 'You need to add column space in order to add more columns to this row.';
        }
      }
    },
    toggleColumn(row, column, colIndex) {
      if (this.viewport === 'desktop') {
        const val = !column.removed;
        // reset columns of removed
        if (val) {
          const coveredByCol = row.columns.filter(c => c.covered[this.viewport] === `i-${colIndex}`);
          _.forEach(coveredByCol, (col) => {
            this.$store.dispatch('setDropletVal', {
              drp: col.covered,
              key: this.viewport,
              val: false,
            });
          });
        }
        this.$store.dispatch('setDropletVal', {
          drp: column,
          key: 'removed',
          val,
        });
        // reset mediaQueries
        const rule = _.find(column.mediaQueries, mq => mq.property === 'grid-area');
        if (rule && rule[this.viewport] !== 'default' && rule[this.viewport] !== 'initial') {
          this.$store.dispatch('changeDropletCss', {
            drp: this.droplet,
            val: this.getInitialGrid(column),
            parent: rule,
            viewport: this.viewport,
          });
        }
        this.$emit('css-generator');
      }
    },
    removeCol(row, column, colIndex) {
      const columns = row.columns;
      const total = columns.length;
      if (total > 1) {
        // first remove column from row
        this.$store.dispatch('spliceDropletVal', {
          array: columns,
          index: colIndex,
        });
        this.$emit('css-generator');
      }
    },
    leaveDrag(e) {
      if (this.dragging) {
        const leftEl = e.target;
        const colIndex = leftEl.getAttribute('data-col-index');
        const rowIndex = leftEl.getAttribute('data-row-index');
        const notHandle = !leftEl.getAttribute('data-drag-handle');
        const handleRowIndex = this.$handle.getAttribute('data-row-index');
        if (colIndex && rowIndex && notHandle && handleRowIndex === rowIndex) {
          const colHidden = leftEl.getAttribute('data-removed');
          if (colHidden && this.allowedCols.includes(~~(colIndex))) {
            // reset when user leaves a removed column
            this.dragHover = {
              rowIndex: null,
              colIndex: null,
            };
          }
        }
      }
    },
    onDragOver(e) {
      if (this.dragging) {
        const hoveredEl = e.toElement || e.target;
        const colIndex = hoveredEl.getAttribute('data-col-index');
        const rowIndex = hoveredEl.getAttribute('data-row-index');
        const notHandle = !hoveredEl.getAttribute('data-drag-handle');
        const handleRowIndex = this.$handle.getAttribute('data-row-index');
        const handleColIndex = ~~(this.$handle.getAttribute('data-col-index'));
        if (colIndex && rowIndex && notHandle && handleRowIndex === rowIndex) {
          // hovered within correct row and only targets area
          const colHidden = hoveredEl.getAttribute('data-removed');
          const cI = ~~(colIndex);
          const rI = ~~(rowIndex);
          const allowHidden = !colHidden && handleColIndex === cI;
          if (this.allowedCols.includes(cI) && (colHidden || allowHidden)) {
            this.dragHover.rowIndex = rI;
            this.dragHover.colIndex = cI;
            this.lastHovered.rowIndex = rI;
            this.lastHovered.colIndex = cI;
            this.allowedDrop = true;
          } else {
            this.allowedDrop = false;
          }
        } else {
          this.allowedDrop = false;
        }
      }
    },
    constructAllowedCols(row) {
      const currentColIndex = ~~(this.$handle.getAttribute('data-col-index'));
      let hiddenList = row.querySelectorAll('[data-removed="true"]');
      hiddenList = [...hiddenList].map(c => ~~(c.getAttribute('data-col-index')));
      if (this.slideDirection === 'right' && hiddenList.length > 0) {
        hiddenList.splice(0, 0, currentColIndex);
        let next = currentColIndex;
        if (hiddenList.includes(next)) {
          hiddenList = hiddenList.filter(l => l >= next);
          const finalList = [];
          for (const n of hiddenList) {
            if (n === next) {
              finalList.push(n);
            } else {
              break;
            }
            next += 1;
          }
          hiddenList = finalList;
        } else {
          hiddenList = [];
        }
      }
      if (this.slideDirection === 'left' && hiddenList.length > 0) {
        hiddenList.push(currentColIndex);
        let prev = currentColIndex;
        if (hiddenList.includes(prev)) {
          hiddenList = hiddenList.filter(l => l <= prev).reverse();
          const finalList = [];
          for (const n of hiddenList) {
            if (n === prev) {
              finalList.push(n);
            } else {
              break;
            }
            prev -= 1;
          }
          hiddenList = finalList;
        } else {
          hiddenList = [];
        }
      }
      return hiddenList;
    },
    startResizeDrag(e, d, cIndex) {
      // a timeout is needed since there is a chrome bug that will immediately
      // fire the dragend event if the DOM is being updated on start (in this
      // case, vue updates the DOM in some scenarios).
      // reference: https://stackoverflow.com/questions/19639969/html5-dragend-event-firing-immediately
      setTimeout(() => {
        this.$handle = e.target;
        this.activeColIndex = cIndex;
        this.startX = 0;
        const row = this.$handle.parentNode.parentNode.parentNode;
        this.slideDirection = d;
        this.allowedCols = this.constructAllowedCols(row);
        this.dragging = true;
      }, 50);
    },
    endResizeDrag(e, column, columns) {
      if (this.dragging) {
        // only allow a column to overlap if there is an available adjacent column
        // or if it gets dropped on top of itself
        if (this.allowedDrop) {
          this.updateColumn({
            column,
            columns,
            endIndex: this.lastHovered.colIndex,
          });
        }
        const parent = e.target.parentNode;
        if (parent.classList.contains('dropletBubble-colPreview-removed')) {
          parent.style.backgroundColor = 'transparent';
        }
        this.$handle.removeEventListener('drag', {});
        this.$handle = null;
        this.activeColIndex = null;
        this.allowedCols = [];
        this.slideDirection = null;
        this.dragHover = {
          rowIndex: null,
          colIndex: null,
        };
        this.lastHovered = {
          rowIndex: null,
          colIndex: null,
        };
        this.allowedDrop = false;
        this.dragging = false;
      }
    },
    getVisibility(column) {
      const source = this.viewport;
      // this is used for visible style on covered columns
      return column.covered[source] || this.viewport !== 'desktop' ? 'hidden' : '';
    },
    shouldShow(column) {
      const source = this.viewport;
      const notCovered = !column.covered[source];
      const draggingOverCovered = this.dragging && column.covered[source] === `i-${this.activeColIndex}`;
      return notCovered || draggingOverCovered;
    },
    showSincher(row, column, cIndex, d) {
      const source = this.viewport;
      const adjacentIndex = d === 'l' ? cIndex - 1 : cIndex + 1;
      const notRemoved = !column.removed;
      const adjacent = row.columns[adjacentIndex];
      const prevIsRemoved = adjacent && adjacent.removed;
      const adjNotCovered = adjacent && !adjacent.covered[source];
      const adjCoveredByCol = adjacent && adjacent.covered[source] === `i-${cIndex}`;
      const notAutoWidth = !this.autoFit[source];
      return notRemoved && prevIsRemoved && notAutoWidth && (adjNotCovered || adjCoveredByCol);
    },
    checkIfInitial(column, cIndex) {
      const source = this.viewport;
      return cIndex === this.activeColIndex ||
             column.covered[source] === `i-${this.activeIndex}` ? this.getInitialGrid(column) : null;
    },
    getInitialGrid(column) {
      return `${column.grid.rowStart} / ${column.grid.colStart} / ${column.grid.rowEnd} / ${column.grid.colEnd}`;
    },
    getBgHover(column, rowIndex, colIndex) {
      const source = this.viewport;
      const dragColIndex = this.$handle ? ~~(this.$handle.getAttribute('data-col-index')) : -1;
      const hasLeftBg = this.slideDirection === 'left' &&
                        column.removed &&
                        rowIndex === this.dragHover.rowIndex &&
                        colIndex >= this.dragHover.colIndex &&
                        colIndex < dragColIndex;
      const hasRightBg = this.slideDirection === 'right' &&
                        column.removed &&
                        rowIndex === this.dragHover.rowIndex &&
                        colIndex <= this.dragHover.colIndex &&
                        colIndex > dragColIndex;
      return hasLeftBg || hasRightBg || column.covered[source] ? 'rgba(255, 255, 255, 0.15)' : '';
    },
    getMinWidth(params) {
      // to get px related values
      let val = params.defaultVal || 0;
      let unit = params.defaultUnit || 'px';
      const rule = this._rule(
        params.$vm.droplet,
        params.$vm.viewport,
        params.selector,
        params.property,
      );
      if (rule.status === 'custom') {
        val = rule.prop[params.$vm.viewport];
      }
      if (rule.status === 'inherit' || rule.status === 'default') {
        val = rule.prop.desktop;
      }
      if (_.isString(val)) {
        const extraction = this.extractVal(val, params.defaultVal, params.defaultUnit);
        val = extraction.val;
        unit = extraction.unit || unit;
      }
      return { val, unit };
    },
    extractVal(v, dV, dU) {
      // for creating the value from css strings
      let numVal = dV;
      let unit = dU;

      if (v.match(/minmax/gi)) {
        const regex = new RegExp('minmax(([^)]+))', 'gi');
        const num = new RegExp(/-?\d+/);
        const mm = v.match(regex)[0].split(',');
        numVal = mm[0].match(num)[0];
        unit = mm[0].split(num)[1];
      }
      return {
        val: _.toNumber(numVal),
        unit,
      };
    },
    setBubbleSize(size) {
      this.$emit('set-bubble-size', size);
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/grid/components/_edit-options/index.js